<template>
  <!--
    Flagi naruszeń pokazywane jako ikony (zamiast długiego tekstu OVERLOAD, OVERSPEED...).
    Dzięki temu wiersz nie rozjeżdża się przy kilku naruszeniach naraz, a opis
    (przetłumaczony) jest dostępny w tooltipie.
  -->
  <div class="violation-flags">
    <template v-if="flags.length">
      <span
          v-for="flag in flags"
          :key="flag.key"
          class="violation-flags__item"
          :title="flag.label"
          :aria-label="flag.label"
          role="img"
      >
        <svg
            class="violation-flags__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
            focusable="false"
            v-html="flag.icon"
        ></svg>
      </span>
    </template>
    <span v-else class="violation-flags__empty" :title="t('no-violations')">-</span>
  </div>
</template>

<script>
// Piktogramy naruszeń — statyczne, rysowane obrysem (currentColor), więc dziedziczą
// kolor z kontekstu (czerwony przy naruszeniu).
const ICONS = {
  // przeciążenie — nacisk (strzałka w dół) na platformę pojazdu
  overload:
      '<path d="M12 2v6"/><path d="M9 5.2 12 8.2 15 5.2"/>' +
      '<rect x="3" y="11" width="18" height="5" rx="1"/>' +
      '<circle cx="7.5" cy="19.5" r="1.8"/><circle cx="16.5" cy="19.5" r="1.8"/>',
  // przekroczenie prędkości — tarcza prędkościomierza ze wskazówką
  overspeed:
      '<path d="M4.5 18a8.5 8.5 0 1 1 15 0"/>' +
      '<path d="M12 18 16.2 13.2"/><circle cx="12" cy="18" r="1.4"/>',
  // przekroczenie wysokości — pojazd „uderzający" w skrajnię (bramownicę)
  overheight:
      '<path d="M3 3h18"/><path d="M5 3v4"/><path d="M19 3v4"/>' +
      '<path d="M12 21V8"/><path d="M8.5 11.5 12 8 15.5 11.5"/>',
  // omijanie / jazda poza pasem — tor przecinający linię pasa
  overlane:
      '<path d="M8 2.5v19" stroke-width="2.2" stroke-dasharray="4 3.2"/>' +
      '<path d="M3 12h16"/><path d="M16 9l3 3-3 3"/>',
  // jazda pod prąd — znak zakazu wjazdu
  wrongDirection: '<circle cx="12" cy="12" r="9"/><path d="M7.5 12h9"/>',
}

export default {
  name: 'ViolationFlags',

  props: {
    vehicle: { type: Object, required: true },
  },

  computed: {
    /**
     * Lista naruszeń pojazdu w postaci gotowej do wyświetlenia:
     * { key, label (tooltip), icon (zawartość SVG) }.
     */
    flags() {
      const v = this.vehicle || {}
      const result = []

      if (this.hasOverload) {
        result.push({
          key: 'overload',
          label: this.overloadLabel,
          icon: ICONS.overload,
        })
      }

      if (v.overspeed) {
        result.push({
          key: 'overspeed',
          label: this.t('violation-overspeed'),
          icon: ICONS.overspeed,
        })
      }

      if (v.overheight) {
        result.push({
          key: 'overheight',
          label: this.t('violation-overheight'),
          icon: ICONS.overheight,
        })
      }

      if (v.overlane) {
        result.push({
          key: 'overlane',
          label: this.t('violation-overlane'),
          icon: ICONS.overlane,
        })
      }

      if (v.wrongDirection) {
        result.push({
          key: 'wrongDirection',
          label: this.t('violation-wrong-direction'),
          icon: ICONS.wrongDirection,
        })
      }

      return result
    },

    // Przeciążenie zbiorczo: flaga ogólna albo któryś z limitów (oś poj./wiel./DMC)
    hasOverload() {
      const v = this.vehicle || {}

      return Boolean(
          v.overload ||
          Number(v.overloadSA || 0) > 0 ||
          Number(v.overloadMA || 0) > 0 ||
          Number(v.overloadTL || 0) > 0
      )
    },

    /**
     * Tooltip przeciążenia doprecyzowany o rodzaj przekroczonego limitu,
     * np. „Przeciążenie (oś poj., DMC)".
     */
    overloadLabel() {
      const v = this.vehicle || {}
      const details = []

      if (Number(v.overloadSA || 0) > 0) details.push(this.t('single-axle'))
      if (Number(v.overloadMA || 0) > 0) details.push(this.t('multiple-axle'))
      if (Number(v.overloadTL || 0) > 0) details.push(this.t('dmc'))

      const base = this.t('violation-overload')

      return details.length ? `${base} (${details.join(', ')})` : base
    },
  },
}
</script>

<style scoped>
.violation-flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 20px;
  line-height: 1;
}

.violation-flags__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;
}

.violation-flags__icon {
  display: block;
  width: 20px;
  height: 20px;
}

.violation-flags__empty {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-md);
}
</style>
